import { useState } from "react";
import { AlertTriangle, GitBranch, Sparkles, ShieldCheck } from "lucide-react";
import { DashFrame, LegendDots, StatusPill, usePalette } from "./kit";

// Colour meaning is fixed across AS-IS and TO-BE: navy = main step, slate = current (AS-IS) process,
// teal = proposed improvement (automated / control), orange = bottleneck, dashed red = exception path.
function stepStyle(step, isSelected, isAsIs, c) {
  if (isSelected) return { background: c.navy, borderColor: c.navy, color: "#fff" };
  if (step.bottleneck) return { background: `${c.orange}1A`, borderColor: c.orange, color: c.orange };
  if (step.exception) return { background: "#fff", borderColor: c.red, color: c.red, borderStyle: "dashed" };
  if (step.automated || step.control) return { background: `${c.teal}14`, borderColor: c.teal, color: c.teal };
  if (isAsIs) return { background: "#fff", borderColor: c.slate, color: c.slate };
  return { background: "#fff", borderColor: c.navy, color: c.navy };
}

function Legend({ isAsIs, c }) {
  return (
    <LegendDots items={[
      isAsIs ? { label: "Current process step", color: c.slate } : { label: "Main step", color: c.navy },
      { label: "Bottleneck", color: c.orange },
      { label: "Exception path", color: c.red, dashed: true },
      { label: "Proposed improvement / control", color: c.teal },
    ]} />
  );
}

function MapView({ map, selected, onSelect, isAsIs, c }) {
  return (
    <div className="overflow-x-auto pb-2">
      <div className="min-w-[720px] space-y-3">
        {map.lanes.map((lane) => (
          <div key={lane.name} className="flex items-stretch gap-3">
            <div className="flex w-32 shrink-0 items-center rounded-xl px-3" style={{ background: `${c.navy}0F` }}>
              <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em]" style={{ color: c.navy }}>{lane.name}</span>
            </div>
            <div className="flex flex-1 items-center gap-2 rounded-xl border border-line bg-cream/50 p-2">
              {lane.steps.map((step, i) => (
                <span key={step.id} className="flex items-center">
                  <button
                    data-testid={`process-step-${step.id}`}
                    onClick={() => onSelect(step)}
                    aria-pressed={selected?.id === step.id}
                    className="flex max-w-[210px] items-center gap-1.5 rounded-full border px-3.5 py-2 text-left text-xs font-medium leading-snug transition-all duration-150 hover:-translate-y-0.5"
                    style={stepStyle(step, selected?.id === step.id, isAsIs, c)}
                  >
                    {step.bottleneck && <AlertTriangle aria-hidden="true" className="h-3 w-3 shrink-0" />}
                    {step.exception && <GitBranch aria-hidden="true" className="h-3 w-3 shrink-0" />}
                    {(step.automated || step.control) && <Sparkles aria-hidden="true" className="h-3 w-3 shrink-0" />}
                    {step.label}
                  </button>
                  {i < lane.steps.length - 1 && <span aria-hidden="true" className="px-1" style={{ color: c.slate }}>→</span>}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ProcessExample({ process }) {
  const [tab, setTab] = useState("asIs");
  const [selected, setSelected] = useState(null);
  const c = usePalette();
  const tabs = [
    ["asIs", "AS-IS map"],
    ["toBe", "TO-BE map"],
    ["requirements", "Requirements"],
    ["stories", "User stories & acceptance criteria"],
    ["uat", "UAT checklist"],
  ];
  const map = tab === "asIs" ? process.asIs : process.toBe;

  return (
    <DashFrame
      title={process.title}
      note="Illustrative reconstruction based on implemented work. AS-IS shows the process before the change; TO-BE shows what was introduced."
      testid="dash-process"
    >
      <div className="flex flex-wrap gap-2" role="tablist" aria-label="Business analysis artefacts">
        {tabs.map(([id, label]) => (
          <button
            key={id}
            role="tab"
            aria-selected={tab === id}
            data-testid={`process-tab-${id}`}
            onClick={() => { setTab(id); setSelected(null); }}
            className="rounded-full border px-4 py-2 text-xs font-semibold transition-colors"
            style={tab === id ? { background: c.navy, borderColor: c.navy, color: "#fff" } : { background: "#fff", borderColor: "#DED5EC", color: "#28272B" }}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {(tab === "asIs" || tab === "toBe") && (
          <>
            <div className="mb-4"><Legend isAsIs={tab === "asIs"} c={c} /></div>
            <MapView map={map} selected={selected} onSelect={setSelected} isAsIs={tab === "asIs"} c={c} />
            <div className="mt-5 min-h-[92px] rounded-xl border border-line bg-cream/70 p-5" data-testid="process-step-detail" style={{ borderLeft: `4px solid ${c.navy}` }}>
              {selected ? (
                <>
                  <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em]" style={{ color: c.navy }}>
                    {selected.label} · responsible: {selected.responsible}
                  </p>
                  <p className="mt-2 text-sm leading-relaxed text-ink">{selected.desc}</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {selected.bottleneck && <StatusPill color={c.orange} icon={AlertTriangle}>Bottleneck</StatusPill>}
                    {selected.exception && <StatusPill color={c.red} icon={GitBranch}>Exception path</StatusPill>}
                    {selected.automated && <StatusPill color={c.teal} icon={Sparkles}>Automated</StatusPill>}
                    {selected.control && <StatusPill color={c.teal} icon={ShieldCheck}>Control retained</StatusPill>}
                  </div>
                </>
              ) : (
                <p className="text-sm text-muted">Click any step in the map to see what happens, who is responsible and where the bottlenecks and exception paths are.</p>
              )}
            </div>
          </>
        )}

        {tab === "requirements" && (
          <div className="overflow-hidden rounded-xl border border-line">
            <table className="w-full border-collapse text-left text-sm">
              <tbody>
                {process.requirements.map(([ref, text]) => (
                  <tr key={ref} className="border-b border-line last:border-0">
                    <td className="w-24 px-4 py-3 font-mono text-xs font-semibold" style={{ background: `${c.navy}0F`, color: c.navy }}>{ref}</td>
                    <td className="px-4 py-3 text-ink">{text}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "stories" && (
          <div className="space-y-4">
            {process.userStories.map((us) => (
              <div key={us.id} className="rounded-xl border border-line bg-cream/50 p-5">
                <p className="text-sm leading-relaxed text-ink">
                  <span className="font-mono text-xs font-semibold" style={{ color: c.navy }}>{us.id} · </span>
                  {us.story}
                </p>
                <ul className="mt-3 space-y-1.5 border-t border-line pt-3">
                  {us.ac.map((a) => (
                    <li key={a} className="text-xs leading-relaxed text-muted">{a}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}

        {tab === "uat" && (
          <div className="overflow-hidden rounded-xl border border-line">
            <table className="w-full min-w-[640px] border-collapse text-left text-sm">
              <thead>
                <tr className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em]" style={{ background: `${c.navy}0F`, color: c.navy }}>
                  <th className="px-4 py-2.5 font-medium">Ref</th>
                  <th className="px-4 py-2.5 font-medium">Scenario</th>
                  <th className="px-4 py-2.5 font-medium">Expected result</th>
                  <th className="px-4 py-2.5 font-medium">Pass / Fail</th>
                </tr>
              </thead>
              <tbody>
                {process.uat.map((row) => (
                  <tr key={row[0]} className="border-t border-line">
                    <td className="px-4 py-3 font-mono text-xs font-semibold" style={{ color: c.navy }}>{row[0]}</td>
                    <td className="px-4 py-3 text-ink">{row[1]}</td>
                    <td className="px-4 py-3 text-muted">{row[2]}</td>
                    <td className="px-4 py-3 font-mono text-xs text-muted">{row[3] || "☐"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashFrame>
  );
}
